import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { BalanceType, UserInfoType } from '../../../api/fetchers/userInfo/types';
import { CoreTelemetryController } from '../../../logger/CoreTelemetryController';
import { fetchUserInfoAction } from '../../apiActions/fetchUserInfoAction';

import type {
  CurrencyInfo,
  DealerType,
  TableCloseNotifyType,
  TableType,
  WinnersTotal,
  WinnerType,
  WinResultType,
} from './types';
import { GameStatus } from './types';

declare global {
  interface Window {
    videoPoster?: string;
  }
}

const initialState: {
  balanceCheckCount: number;
  currency: string | null;
  currencyInfo: CurrencyInfo | null;
  currentStatus: GameStatus | string;
  dealer: DealerType | null;
  gameAbandoned: boolean;
  gameChannel: string | null;
  gameId: string | null;
  gameName: string | null;
  gamePaused: boolean;
  gameSuspended: boolean;
  hideUI: boolean;
  inactivityCounter: number;
  isLoadedInActiveRound: boolean;
  leftDrawerOpened: string | null; // TODO: do same with right drawer
  netPosition: number;
  table: TableType | null;
  tableCloseNotify: TableCloseNotifyType | null;
  timer: number;
  timerStartTime: number;
  userInfo: UserInfoType | null;
  videoPoster: string;
  winResult: WinResultType | null;
  winners: WinnerType[];
  winnersTotal: WinnersTotal | null;
  waitForWinResult: boolean;
} = {
  balanceCheckCount: 4,
  currency: null,
  currencyInfo: null,
  currentStatus: GameStatus.WaitForNextGame,
  dealer: null,
  gameAbandoned: false,
  gameChannel: null,
  gameId: null,
  gameName: null,
  gamePaused: false,
  gameSuspended: false,
  hideUI: false,
  inactivityCounter: 0,
  isLoadedInActiveRound: true,
  leftDrawerOpened: null,
  netPosition: 0,
  table: null,
  tableCloseNotify: null,
  timer: 0,
  timerStartTime: 0,
  userInfo: null,
  videoPoster: window.videoPoster || '',
  winResult: null,
  winners: [],
  winnersTotal: null,
  waitForWinResult: true,
};

export const gameStateSlice = createSlice({
  name: 'gameState',
  initialState,
  reducers: {
    setWinners: (state, action: PayloadAction<WinnerType[]>) => {
      state.winners = action.payload;
    },
    setWinnersAndTotal(state, action: PayloadAction<{ winners: WinnerType[]; total: WinnersTotal }>) {
      state.winners = action.payload.winners;
      state.winnersTotal = action.payload.total;
    },
    setWinResult: (state, action: PayloadAction<WinResultType | null>) => {
      state.winResult = action.payload;
    },
    setCurrentGameStatus: (state, action: PayloadAction<GameStatus | string>) => {
      state.currentStatus = action.payload;
      CoreTelemetryController.setGameState(action.payload);
    },
    resetCurrentGameStatus: (state) => {
      state.currentStatus = GameStatus.WaitForNextGame;
    },
    setTimer: (state, action: PayloadAction<number>) => {
      state.timer = action.payload;
      state.timerStartTime = Date.now();
    },
    setGameId: (state, action: PayloadAction<string>) => {
      state.gameId = action.payload;
    },
    setGameChannel: (state, action: PayloadAction<string>) => {
      state.gameChannel = action.payload;
    },
    setGameName: (state, action: PayloadAction<string>) => {
      state.gameName = action.payload;
    },
    setHideUI: (state, action: PayloadAction<boolean>) => {
      state.hideUI = action.payload;
    },
    setUserInfo: (state, action: PayloadAction<UserInfoType>) => {
      state.userInfo = action.payload;
      state.currency = action.payload.balance.currency;
    },
    setBalance: (state, action: PayloadAction<number>) => {
      if (state.userInfo?.balance) {
        state.userInfo.balance.amount = action.payload;
      }
    },
    updateBalance: (state, action: PayloadAction<Partial<BalanceType>>) => {
      if (state.userInfo?.balance) {
        state.userInfo.balance = { ...state.userInfo.balance, ...action.payload };
      }
    },
    setCurrency: (state, action: PayloadAction<string>) => {
      state.currency = action.payload;
    },
    setCurrencyInfo: (state, action: PayloadAction<CurrencyInfo>) => {
      state.currencyInfo = action.payload;
    },
    setInactivityCounter: (state, action: PayloadAction<number>) => {
      state.inactivityCounter = action.payload;
    },
    setNetPosition: (state, action: PayloadAction<number>) => {
      state.netPosition = action.payload;
    },
    setOpenLeftDrawer: (state, action: PayloadAction<string | null>) => {
      state.leftDrawerOpened = action.payload;
    },
    setDealer: (state, action: PayloadAction<DealerType>) => {
      state.dealer = action.payload;
    },
    setTable: (state, action: PayloadAction<TableType>) => {
      state.table = action.payload;
    },
    setBalanceCheckCount: (state, action: PayloadAction<number>) => {
      state.balanceCheckCount = action.payload;
    },
    setGamePaused: (state, action: PayloadAction<boolean>) => {
      state.gamePaused = action.payload;
    },
    setGameAbandoned: (state, action: PayloadAction<boolean>) => {
      state.gameAbandoned = action.payload;
    },
    setGameSuspended: (state, action: PayloadAction<boolean>) => {
      state.gameSuspended = action.payload;
    },
    setIsLoadedInActiveRound: (state, action: PayloadAction<boolean>) => {
      state.isLoadedInActiveRound = action.payload;
    },
    setVideoPoster: (state, action: PayloadAction<string>) => {
      state.videoPoster = action.payload;
    },
    resetForNewGame: (state) => {
      state.winResult = null;
    },
    setTableCloseNotify: (state, action: PayloadAction<TableCloseNotifyType>) => {
      state.tableCloseNotify = action.payload;
    },
    setWaitForWinResult: (state, action: PayloadAction<boolean>) => {
      state.waitForWinResult = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserInfoAction.fulfilled, (state, action) => {
      state.userInfo = action.payload.parsedResponse;
      const {
        balance: { currency },
      } = action.payload.parsedResponse;
      state.currency = currency;
    });
  },
});

export const gameStateActions = gameStateSlice.actions;
